import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for elegant scroll-triggered reveal animations.
 * Uses IntersectionObserver to detect when elements enter the viewport.
 * 
 * @param {Object} options
 * @param {number} options.threshold - Visibility threshold (0-1), default 0.15
 * @param {string} options.rootMargin - Root margin for earlier/later trigger
 * @returns {{ ref: React.RefObject, isRevealed: boolean }}
 */
const useScrollReveal = (options = {}) => {
  const { threshold = 0.15, rootMargin = '0px 0px -60px 0px' } = options;
  const ref = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          observer.unobserve(element); // Only animate once
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin]);

  return { ref, isRevealed };
};

export default useScrollReveal;
